import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  VStack,
  Button,
  Box,
  HStack,
  Text,
  Collapse,
  Tooltip,
  Divider,
  Icon,
  Flex,
  SimpleGrid,
  ButtonGroup,
  Menu,
  StyledAvatar,
} from '@televet/kibble-ui';
import { PhoneCallState } from 'shared/providers/PhoneCallProvider/interfaces/PhoneCallState';
import PhoneDialPad from './components/PhoneDialPad';
import { StagePhoneCallProps } from 'shared/providers/PhoneCallProvider/interfaces/StagePhoneCallProps';
import { formatPhoneNumber, validatePhoneNumber } from 'shared/utils';
import { useAppDispatch } from 'state/hooks';
import { openChannelOverlay } from 'pages/Conversations/state/conversationsSlice';
import ElapsedTime from 'shared/components/ElapsedTime';
import {
  CallDirection,
  CallStatus,
  CallProvider,
  useGetOrCreateActiveClientChannelMutation,
  GetPhoneCallParticipantDocument,
  IgnoreCallAction,
  CallParticipantResult,
} from 'shared/types/graphql';
import MicrophoneBlockedAlert from './components/MicrophoneBlockedAlert';
import partial from 'lodash-es/partial';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useSidePanelSearch } from '../SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { PhoneDisplayMode } from 'shared/providers/PhoneCallProvider/state/phoneSlice';
import { Rnd } from 'react-rnd';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import PhoneCallParticipantDetails from './components/PhoneCallParticipantDetails';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiMemoEvents } from 'shared/providers/TrackingProvider/EventNames';

export const UNKNOWN_DISPLAY_NAME = 'Unknown';
const POPOVER_DEFAULT = { width: 380, height: 'auto', x: window.innerWidth / 2 - 380 / 2, y: 40 };

const ignoreCallActionLabels = {
  [IgnoreCallAction.Ignore]: 'Ignore',
  [IgnoreCallAction.IgnoreAndSendChannelMessage]: 'Ignore and send message',
};

const callProviderStatusLabels: Record<CallProvider, Partial<Record<CallStatus, string>>> = {
  [CallProvider.Axion]: {
    [CallStatus.Canceled]: 'Call Ended',
    [CallStatus.Completed]: 'Call Ended',
    [CallStatus.NoAnswer]: 'Call Ended',
  },
  [CallProvider.Twilio]: {
    [CallStatus.Canceled]: 'Call Declined',
    [CallStatus.Completed]: 'Call Ended',
    [CallStatus.NoAnswer]: 'Call Missed',
  },
};

const callParticipantResultLabels: Record<CallParticipantResult, string> = {
  [CallParticipantResult.Ignored]: 'Call Ignored',
  [CallParticipantResult.IgnoredWithMessage]: 'Call Ignored With Message',
  [CallParticipantResult.Rejected]: 'Call Rejected',
  [CallParticipantResult.Answered]: 'Call Answered',
};

interface IPhoneCallPopoverProps {
  phoneCallState: PhoneCallState;
  isCallOngoing: boolean;
  callProvider: CallProvider;
  displayMode: PhoneDisplayMode;
  isMicrophoneBlocked: boolean;
  onToggleMute: () => void;
  onToggleRecording: () => void;
  onToggleDialPad: () => void;
  onPhoneNumberInputChange: (value: string) => void;
  onClearCallParticipant: () => void;
  onSendDigits: (digits: string) => void;
  onStartPhoneCall: (props: StagePhoneCallProps) => void;
  onAcceptPhoneCall: () => void;
  onIgnorePhoneCall: (action: IgnoreCallAction) => void;
  onEndPhoneCall: () => void;
  onToggleMinimized: () => void;
  onClosePopover: () => void;
}

export const composeMissedCallMessage = ({ clinicName }: { clinicName?: string }): string => {
  return `Thank you for contacting ${clinicName ?? 'our clinic'
    }. Sorry we missed your call. Feel free to reply with any additional details or questions and a member of our team will respond as soon as possible.`;
};

// TODO (future): Reintroduce specific error messaging
// const twilioVoiceErrorCodes: Record<number, string> = {
//   53405:
//     "Phone call unable to connect: Please check your network's firewall and security settings to ensure the browser's ability to connect.",
//   31401: 'Please make sure your microphone permissions for Otto are enabled in your browser settings.',
// };

const PhoneCallPopover = ({
  phoneCallState,
  isCallOngoing,
  callProvider,
  displayMode,
  isMicrophoneBlocked,
  onToggleMute,
  onToggleRecording,
  onToggleDialPad,
  onPhoneNumberInputChange,
  onClearCallParticipant,
  onSendDigits,
  onStartPhoneCall,
  onAcceptPhoneCall,
  onIgnorePhoneCall,
  onEndPhoneCall,
  onToggleMinimized,
  onClosePopover,
}: IPhoneCallPopoverProps): JSX.Element => {
  const { currentClinic } = useClinicUser();
  const dispatch = useAppDispatch();
  const { isFeatureEnabled } = useFeatureFlag();
  const { viewClinicPetParent, viewClinicPet } = useSidePanelSearch();
  const { trackEvent } = useTracking();
  const [getOrCreateActiveClientChannel] = useGetOrCreateActiveClientChannelMutation();
  const [isParticipantDetailsExpanding, setIsParticipantDetailsExpanding] = useState(false);

  const isTwilioNewPhoneCallExperienceEnabled = isFeatureEnabled(FeatureFlagName.TwilioNewPhoneCallExperience);
  const participantPhoneNumber = phoneCallState.participant.phoneNumber || phoneCallState.toPhoneNumberInput || '';
  const isValidPhoneNumber = !!validatePhoneNumber(participantPhoneNumber);

  const handleStartCall = (): void => {
    if (participantPhoneNumber && isValidPhoneNumber) {
      onStartPhoneCall({ phoneNumber: participantPhoneNumber, isCallWithRecording: false });
    }
  };

  const handleCloseButtonClick = (): void => {
    onClosePopover();
  };

  const handleMinimizeButtonClick = (): void => {
    onToggleMinimized();
  };

  const onConversationClick = (channelId: string): void => {
    dispatch(openChannelOverlay({ channelId }));
  };

  const handleParticipantClick = (): void => {
    const clinicPetParentId = phoneCallState.participant?.clinicPetParentId;
    if (clinicPetParentId) {
      viewClinicPetParent({ clinicPetParentId, forceOpen: true });
    }
  };

  const onPetClick = (clinicPetId: string): void => {
    viewClinicPet({ clinicPetId, forceOpen: true });
  };

  const handleSendMessageClick = useCallback(async (): Promise<void> => {
    const clinicPetParentId = phoneCallState.participant?.clinicPetParentId;
    if (clinicPetParentId) {
      let channelId = phoneCallState.participant?.channel?.id;

      const { data } = await getOrCreateActiveClientChannel({
        variables: { data: { clinicPetParentIds: [clinicPetParentId] } },
        refetchQueries: [GetPhoneCallParticipantDocument],
      });
      channelId = data?.getOrCreateActiveClientChannel?.id;

      if (channelId) {
        let draftMessageText;
        if (
          callProvider === CallProvider.Axion &&
          phoneCallState.direction === CallDirection.Inbound &&
          phoneCallState.status === CallStatus.NoAnswer
        ) {
          draftMessageText = composeMissedCallMessage({ clinicName: currentClinic?.name });
        }

        dispatch(
          openChannelOverlay({
            channelId,
            draftMessageText,
          }),
        );
      }
    }
  }, [currentClinic, callProvider, phoneCallState, dispatch, getOrCreateActiveClientChannel]);

  const showIncomingCallAlert =
    phoneCallState.direction === CallDirection.Inbound && phoneCallState.status === CallStatus.Ringing;
  const showEndCallButton =
    phoneCallState.status === CallStatus.InProgress ||
    (phoneCallState.direction === CallDirection.Outbound &&
      phoneCallState.status &&
      [CallStatus.Queued, CallStatus.Ringing].includes(phoneCallState.status));
  const showCallParticipantDetails =
    phoneCallState.participant.clinicPetParentId &&
    (callProvider === CallProvider.Axion ||
      (callProvider === CallProvider.Twilio && isTwilioNewPhoneCallExperienceEnabled));
  const hasParticipant = !!phoneCallState.participant.clinicPetParentId || !!phoneCallState.participant.phoneNumber;
  const avatarDisplayName =
    !phoneCallState.participant.displayName || phoneCallState.participant.displayName === UNKNOWN_DISPLAY_NAME
      ? undefined
      : phoneCallState.participant.displayName;

  const phoneStatusLabel = useMemo(() => {
    switch (phoneCallState.status) {
      case CallStatus.Ringing:
        return phoneCallState.direction === CallDirection.Outbound ? 'Calling...' : 'Incoming Call';
      case CallStatus.InProgress:
        return <ElapsedTime startTime={phoneCallState.startTime} />;
      default:
        return 'Ready';
    }
  }, [phoneCallState]);

  const closeButton = (
    <Button
      iconName="close"
      sx={showIncomingCallAlert ? { '& svg': { color: 'text.success' } } : {}}
      className="cancelDrag"
      variant="ghost"
      size="sm"
      p={0}
      paddingInline={0}
      paddingInlineStart={0}
      borderRadius="lg"
      onClick={handleCloseButtonClick}
    />
  );


  const postCallStatus = useMemo(() => {
    const callStatusLabels = callProviderStatusLabels[callProvider];
    let callStatusLabel = !phoneCallState.status ? '' : callStatusLabels[phoneCallState.status] ?? '';
    if (phoneCallState.status === CallStatus.Canceled && phoneCallState.isIgnoreMessageSent) {
      callStatusLabel += ' With Message';
    }
    if (phoneCallState.result) {
      callStatusLabel = callParticipantResultLabels[phoneCallState.result];
    }
    return (
      <>
        <Text size="md" variant="subtle" lineHeight={1.3}>
          {callStatusLabel}
        </Text>
        {phoneCallState.participant.clinicPetParentId ? (
          <Button variant="secondarySubtle" size="sm" iconName="message" onClick={handleSendMessageClick}>
            Send Message
          </Button>
        ) : null}
      </>
    );
  }, [callProvider, phoneCallState, handleSendMessageClick]);

  const handleCollapseStart = (): void => {
    setIsParticipantDetailsExpanding(true);
  };
  const handleCollapseEnd = (): void => {
    setTimeout(() => {
      setIsParticipantDetailsExpanding(false);
    }, 300);
  };

  const rnd = useRef<Rnd | null>(null);
  const resizeContainerRef = useRef<HTMLDivElement | null>(null);
  const bodyContainerRef = useRef<HTMLDivElement | null>(null);

  const resizePopover = useCallback((): void => {
    const resizeContainer = resizeContainerRef.current;
    const bodyContainer = bodyContainerRef.current;
    if (!resizeContainer || !bodyContainer) return;

    const bodyRect = bodyContainer.getBoundingClientRect();
    const contentH = bodyContainer.scrollHeight + 2;
    const availableH = window.innerHeight - bodyRect.top + 2;
    const newHeight = Math.min(contentH, availableH);

    // only write if it actually changed
    if (resizeContainer.offsetHeight !== newHeight) {
      resizeContainer.style.height = `${newHeight}px`;
    }
  }, []);

  useLayoutEffect(() => {
    const bodyContainer = bodyContainerRef.current;
    const observer = new ResizeObserver(() => {
      resizePopover();
    });
    if (bodyContainer) {
      observer.observe(bodyContainer);
    }
    return (): void => {
      if (bodyContainer) {
        observer.unobserve(bodyContainer);
      }
    };
  }, [resizePopover]);

  return (
    <Rnd
      className="PhoneCallPopover__Rnd"
      ref={rnd}
      default={POPOVER_DEFAULT}
      onDrag={resizePopover}
      style={{ zIndex: 'var(--chakra-zIndices-modal)' }}
      bounds="body"
      enableResizing={false}
      enableUserSelectHack={true}
      dragHandleClassName="dragHandle"
      cancel=".cancelDrag"
    >
      <Box
        className="PhoneCallPopover__ResizeContainer"
        ref={resizeContainerRef}
        bg="background.overlay"
        borderRadius="lg"
        border="1px solid"
        borderColor="border.default"
        boxShadow="2xl"
        position="relative"
        zIndex="modal"
        overflow={isParticipantDetailsExpanding ? 'hidden' : 'auto'}
      >
        <Box className="PhoneCallPopover__BodyContainer" ref={bodyContainerRef} overflow="hidden">
          {callProvider === CallProvider.Twilio && isMicrophoneBlocked ? (
            <MicrophoneBlockedAlert closeButton={closeButton} />
          ) : (
              <VStack className="PhoneCallPopover__Body" align="stretch" spacing={4} pb={3}>
                <SimpleGrid
                  className="PhoneCallPopover__StatusIndicator dragHandle"
                  bg={showIncomingCallAlert ? 'background.success' : 'background.info'}
                  p={1}
                  columns={3}
                  minH="40px"
                  alignItems="center"
                  position="relative"
                  cursor="move"
                >
                  <Flex align="center" justify="flex-start" pl={2}>
                    <Icon name="grid" size="sm" variant={showIncomingCallAlert ? 'success' : 'interactive'} />
                  </Flex>
                  <HStack spacing={1} align="center" justify="center">
                    {phoneCallState.isRecording && isCallOngoing && (
                      <Tooltip label="This call is being recorded" placement="top">
                        <Icon name="record" variant="danger" size="md" />
                      </Tooltip>
                    )}
                    <Text color={showIncomingCallAlert ? 'text.success' : 'text.info'}>{phoneStatusLabel}</Text>
                  </HStack>
                  <Flex justify="flex-end">
                    {!(isCallOngoing && callProvider === CallProvider.Twilio) ? closeButton : null}
                  </Flex>
                </SimpleGrid>
                <HStack className="PhoneCallPopover__CallContainer" justify="space-between" flex="1" w="100%" px={4}>
                  <HStack className="PhoneCallPopover__CallParticipant">
                    {hasParticipant ? (
                      <StyledAvatar
                        name={avatarDisplayName}
                        showTooltip={false}
                        size="lg"
                        cursor="pointer"
                        onClick={handleParticipantClick}
                      />
                    ) : callProvider === CallProvider.Axion ? (
                      <Text variant="subtle">No recent phone calls</Text>
                    ) : null}
                    <VStack align="flex-start" spacing={0}>
                      <Text size="lg" fontWeight="bold" lineHeight={1.3}>
                        {phoneCallState.participant?.displayName}
                      </Text>
                      <Text size="md" variant="subtle" lineHeight={1.3}>
                        {formatPhoneNumber(phoneCallState.participant?.phoneNumber)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack className="PhoneCallPopover__CallControls">
                    {!isCallOngoing && hasParticipant ? (
                      <>
                        {callProvider === CallProvider.Twilio && !!phoneCallState.status && (
                          <Tooltip label="Call Again" openDelay={600}>
                            <Button
                              iconName="phone"
                              size="sm"
                              variant="ghostNeutral"
                              isDisabled={!isValidPhoneNumber}
                              onClick={handleStartCall}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Reset Call" openDelay={600}>
                          <Button
                            iconName="closeCircle"
                            size="sm"
                            variant="ghostNeutral"
                            onClick={onClearCallParticipant}
                          />
                        </Tooltip>
                      </>
                    ) : null}
                    {callProvider === CallProvider.Twilio && (
                      <>
                        <Tooltip
                          label={phoneCallState.isMuted ? 'Unmute (Currently muted)' : 'Mute (Currently unmuted)'}
                          openDelay={600}
                        >
                          <Button
                            iconName={phoneCallState.isMuted ? 'microphoneMute' : 'microphone'}
                            size="sm"
                            variant="ghostNeutral"
                            isActive={phoneCallState.isMuted}
                            onClick={onToggleMute}
                          />
                        </Tooltip>
                        <Tooltip label={`${phoneCallState.isDialPadOpen ? 'Hide' : 'Show'} Dial Pad`} openDelay={600}>
                          <Button
                            iconName="phoneButtons"
                            size="sm"
                            variant="ghostNeutral"
                            isActive={phoneCallState.isDialPadOpen}
                            onClick={onToggleDialPad}
                          />
                        </Tooltip>
                      </>
                    )}
                  </HStack>
                </HStack>

                {callProvider === CallProvider.Twilio && (
                  <Collapse
                    in={phoneCallState.isDialPadOpen}
                    transition={{ exit: { duration: 0.3 }, enter: { duration: 0.3 } }}
                    onAnimationStart={handleCollapseStart}
                    onAnimationComplete={handleCollapseEnd}
                  >
                    <Box pt={1} px={4}>
                      <PhoneDialPad
                        isDialPadOpen={phoneCallState.isDialPadOpen}
                        toPhoneNumberInput={phoneCallState.toPhoneNumberInput}
                        isCallInProgress={isCallOngoing}
                        onPhoneNumberInputChange={onPhoneNumberInputChange}
                        onSendDigits={onSendDigits}
                      />
                    </Box>
                  </Collapse>
                )}

                <HStack className="PhoneCallPopover__ActionButtonsContainer" px={4} pt={2} pb={1} justify="end">
                  {showEndCallButton ? (
                    <Flex className="PhoneCallPopover__InProgressActions" w="100%" justify="space-between">
                      <Box>
                        {phoneCallState.direction === CallDirection.Outbound &&
                          isCallOngoing &&
                          phoneCallState.isRecording &&
                          callProvider === CallProvider.Twilio && (
                            <Button
                              iconName="stopRecording"
                              size="sm"
                              variant="secondarySubtle"
                              onClick={onToggleRecording}
                            >
                              Stop Recording
                            </Button>
                          )}
                      </Box>
                      <Button
                        iconName="phoneCancel"
                        variant="primaryDestructive"
                        size="sm"
                        onClick={(): void => {
                          onEndPhoneCall();
                        }}
                      >
                        End Call
                    </Button>
                    </Flex>
                  ) : phoneCallState.direction === CallDirection.Outbound ? (
                    <Flex className="PhoneCallPopover__OutboundActions" w="100%" justify="space-between" align="center">
                      {callProvider === CallProvider.Twilio && !phoneCallState.status ? (
                        <>
                          <Button
                            leftIcon={<Icon name="record" size="sm" />}
                            size="sm"
                            variant={phoneCallState.isRecording ? 'secondaryDestructive' : 'secondarySubtle'}
                            onClick={(): void => {
                              trackEvent(AiMemoEvents.Record);
                              onToggleRecording();
                            }}
                          >
                            Record
                        </Button>
                          <Button
                            flex="0 0 auto"
                            iconName="phone"
                            variant="primary"
                            size="sm"
                            isDisabled={!isValidPhoneNumber}
                            onClick={handleStartCall}
                          >
                            Start Call
                        </Button>
                        </>
                      ) : !!phoneCallState.status ? (
                        postCallStatus
                      ) : null}
                    </Flex>
                  ) : phoneCallState.direction === CallDirection.Inbound ? (
                    <HStack className="PhoneCallPopover__InboundActions" w="100%" justify="space-between">
                      {isCallOngoing && !phoneCallState.result ? (
                        <ButtonGroup variant="primaryDestructive" isAttached>
                          <Button
                            iconName="bellCancel"
                            variant="secondarySubtle"
                            size="sm"
                            onClick={partial(onIgnorePhoneCall, IgnoreCallAction.Ignore)}
                          >
                            Ignore
                        </Button>
                          {phoneCallState.participant.clinicPetParentId ? (
                            <Menu
                              buttonProps={{
                                leftIconName: 'chevronDown',
                                showRightIcon: false,
                                variant: 'secondarySubtle',
                                size: 'sm',
                                borderLeft: 'none',
                              }}
                              placement="bottom"
                              listProps={{
                                menuItems: [
                                  {
                                    label: ignoreCallActionLabels[IgnoreCallAction.Ignore],
                                    leftElement: <Icon name="bellCancel" size="sm" />,
                                    value: IgnoreCallAction.Ignore,
                                    onSelect: partial(onIgnorePhoneCall, IgnoreCallAction.Ignore),
                                  },
                                  {
                                    label: ignoreCallActionLabels[IgnoreCallAction.IgnoreAndSendChannelMessage],
                                    leftElement: <Icon name="message" size="sm" />,
                                    value: IgnoreCallAction.IgnoreAndSendChannelMessage,
                                    onSelect: partial(onIgnorePhoneCall, IgnoreCallAction.IgnoreAndSendChannelMessage),
                                  },
                                ],
                              }}
                            />
                          ) : null}
                        </ButtonGroup>
                      ) : (
                          postCallStatus
                        )}

                      {callProvider === CallProvider.Twilio && (
                        <Button iconName="phone" variant="primary" size="sm" onClick={onAcceptPhoneCall}>
                          Accept
                        </Button>
                      )}
                    </HStack>
                  ) : null}
                </HStack>
                {showCallParticipantDetails ? (
                  <>
                    <HStack spacing={1}>
                      <Divider zIndex="0" />
                      <Button flex="0 0 auto" variant="tertiary" size="xs" zIndex="1" onClick={handleMinimizeButtonClick}>
                        {displayMode === PhoneDisplayMode.Maximized ? 'Hide' : 'Show'} details
                    </Button>
                      <Divider />
                    </HStack>
                    <Collapse in={displayMode === PhoneDisplayMode.Maximized}
                      transition={{ exit: { duration: 0.3 }, enter: { duration: 0.3 } }}
                      onAnimationStart={handleCollapseStart}
                      onAnimationComplete={handleCollapseEnd}
                    >
                      <PhoneCallParticipantDetails
                        phoneCallState={phoneCallState}
                        onPetClick={onPetClick}
                        onConversationClick={onConversationClick}
                      />
                    </Collapse>
                  </>
                ) : null}
              </VStack>
            )}
        </Box>
      </Box>
    </Rnd>
  );
};

export default PhoneCallPopover;
